import { useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { BadgeCheck, CloudCog, Flame, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import { useOnboardingReturn } from '@/hooks/use-onboarding-return'
import { useMyPermissions } from '@/hooks/use-settings'
import {
  CHANNEL_TILE_CLASS,
  ChannelIcon,
  channelBandStyle,
  useChannelStats,
  type ChannelDef,
  type ChannelKind,
  type ChannelStats,
} from './channel-catalog'

type Filter = 'all' | ChannelKind | 'beta'

/**
 * Trang "Tất cả kênh": ô chỉ số + lưới thẻ kênh, theo mẫu SaleSmartly
 * `/connect/channels/all`.
 */
export function ChannelsOverviewPage() {
  const { t } = useTranslation('chanCatalog')
  const { stats, channels } = useChannelStats()
  const [filter, setFilter] = useState<Filter>('all')

  const sum = useMemo(() => {
    const acc = { official: { total: 0, active: 0 }, personal: { total: 0, active: 0 } }
    for (const c of channels) {
      acc[c.kind].total += stats[c.key]?.total ?? 0
      acc[c.kind].active += stats[c.key]?.active ?? 0
    }
    return acc
  }, [channels, stats])

  const shown =
    filter === 'all'
      ? channels
      : filter === 'beta'
        ? channels.filter((c) => c.comingSoon)
        : channels.filter((c) => c.kind === filter && !c.comingSoon)

  return (
    <div className="space-y-5">
      <h1 className="flex h-14 items-center text-lg font-medium">{t('overview.title')}</h1>

      <div className="grid gap-4 md:grid-cols-2">
        <MetricCard
          label={t('overview.officialLabel')}
          hint={t('overview.officialHint')}
          value={`${sum.official.active}/${sum.official.total}`}
          icon={<BadgeCheck />}
        />
        <MetricCard
          label={t('overview.personalLabel')}
          hint={t('overview.personalHint')}
          value={`${sum.personal.active}/${sum.personal.total}`}
          icon={<CloudCog />}
          tone="violet"
        />
      </div>

      <Tabs value={filter} onValueChange={(v) => setFilter(v as Filter)}>
        <div className="w-full overflow-x-auto">
          <TabsList>
            <TabsTrigger value="all">{t('overview.tabs.all')}</TabsTrigger>
            <TabsTrigger value="official">{t('overview.tabs.official')}</TabsTrigger>
            <TabsTrigger value="personal">{t('overview.tabs.personal')}</TabsTrigger>
            <TabsTrigger value="beta">{t('overview.tabs.comingSoon')}</TabsTrigger>
          </TabsList>
        </div>
      </Tabs>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
        {shown.map((c) =>
          c.comingSoon ? <ComingSoonTile key={c.key} def={c} /> : <ChannelTile key={c.key} def={c} stats={stats[c.key]} />,
        )}
      </div>
    </div>
  )
}

function MetricCard({
  label,
  hint,
  value,
  icon,
  tone = 'blue',
}: {
  label: string
  hint: string
  value: string
  icon: ReactNode
  tone?: 'blue' | 'violet'
}) {
  return (
    <div
      className={
        'relative overflow-hidden rounded-lg border p-4 ' +
        (tone === 'blue'
          ? 'border-primary/10 bg-gradient-to-r from-primary/[0.07] via-primary/[0.01] to-primary/[0.07]'
          : 'border-violet-500/10 bg-gradient-to-r from-violet-500/[0.07] via-violet-500/[0.01] to-violet-500/[0.07]')
      }
    >
      <p className="text-sm text-muted-foreground" title={hint}>
        {label}
      </p>
      <p className="mt-2 text-2xl font-medium tabular-nums">{value}</p>
      <span
        className={
          'pointer-events-none absolute right-6 top-1/2 -translate-y-1/2 opacity-30 [&_svg]:h-12 [&_svg]:w-12 [&_svg]:stroke-[1.25] ' +
          (tone === 'blue' ? 'text-primary' : 'text-violet-500')
        }
      >
        {icon}
      </span>
    </div>
  )
}

/** Kênh chưa làm ở đợt này — thẻ mờ, không bấm được. */
function ComingSoonTile({ def }: { def: ChannelDef }) {
  const { t } = useTranslation('chanCatalog')
  return (
    <div className="relative flex h-[170px] cursor-not-allowed select-none flex-col rounded-lg border bg-muted/40 p-4 opacity-60 grayscale">
      <span className="absolute right-0 top-0 rounded-bl-lg rounded-tr-lg bg-muted px-2 py-0.5 text-xs text-muted-foreground">
        {t('overview.comingSoon')}
      </span>
      <span className="flex items-center gap-2.5">
        <ChannelIcon def={def} />
        <span className="truncate font-medium">{def.name}</span>
      </span>
      <p className="mt-3 line-clamp-2 flex-1 text-xs leading-5 text-muted-foreground">{def.description}</p>
      <div className="flex justify-end">
        <Button variant="secondary" disabled>
          {t('overview.comingSoon')}
        </Button>
      </div>
    </div>
  )
}

function ChannelTile({ def, stats }: { def: ChannelDef; stats?: ChannelStats }) {
  const { t } = useTranslation('chanCatalog')
  const navigate = useNavigate()
  // Đang trong luồng trang Bắt đầu ("Xem tất cả các kênh") → giữ from/step sang trang kênh.
  const { carry } = useOnboardingReturn()
  const total = stats?.total ?? 0
  // Nút "Thêm" = kết nối kênh mới → cần `integrations.create` (Quản lý / Nhân viên mặc định không có).
  const canAdd = !!useMyPermissions().data?.has('integrations.create')
  const go = (add?: boolean) => navigate(carry(`/integrations/${def.key}${add ? '?add=1' : ''}`, 1))

  return (
    <div className={cn(CHANNEL_TILE_CLASS, 'h-[170px]')} style={channelBandStyle(def)}>
      {def.tag && (
        <span
          className={
            'absolute right-0 top-0 flex items-center gap-1 rounded-bl-lg rounded-tr-lg px-2 py-0.5 text-xs ' +
            (def.tag === 'popular' ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary')
          }
        >
          {def.tag === 'popular' ? <Flame className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />}
          {t(def.tag === 'popular' ? 'overview.tagPopular' : 'overview.tagBeta')}
        </span>
      )}

      <button type="button" onClick={() => go()} className="flex items-center gap-2.5 text-left">
        <ChannelIcon def={def} />
        <span className="truncate font-medium">{def.name}</span>
      </button>

      <div className="mt-3 flex-1 text-xs leading-5 text-muted-foreground">
        {total > 0 ? (
          <>
            <p>{t('overview.connected', { count: total })}</p>
            <p className="font-medium text-foreground">{t('overview.active', { count: stats?.active ?? 0 })}</p>
          </>
        ) : (
          <p className="line-clamp-2">{def.description}</p>
        )}
      </div>

      <div className="flex justify-end gap-2">
        {canAdd && (
          <Button variant="secondary" onClick={() => go(true)}>
            {t('common:actions.add')}
          </Button>
        )}
        {total > 0 && (
          <Button variant="secondary" onClick={() => go()}>
            {t('common:actions.manage')}
          </Button>
        )}
      </div>
    </div>
  )
}
